export interface StorySection {
  heading: string;
  paragraphs: string[];
}

export interface Story {
  id: string;
  title: string;
  category: string;
  author: string;
  role: string;
  date: string;
  readTime: string;
  excerpt: string;
  image: string;
  tags: string[];
  sections: StorySection[];
  impacts: string[];
  quote: string;
}

export const storyCategories = [
  "全部",
  "言语围攻",
  "人肉搜索",
  "恶意剪辑",
  "造谣诽谤",
  "举报封禁",
  "舆论株连",
];

export const stories: Story[] = [
  {
    id: "ten-years-of-lesson-plans",
    title: "我删掉了十年的教案，只为让那个账号消失",
    category: "人肉搜索",
    author: "程曦（化名）",
    role: "中学语文教师",
    date: "2024-11-08",
    readTime: "12 分钟",
    excerpt:
      "一条被断章取义的课堂片段，让我的姓名、学校和家庭住址在三天内传遍了好几个平台。我删掉了十年积累的教案，以为删干净了，事情就会停下来。",
    image:
      "https://readdy.ai/api/search-image?query=conceptual%20editorial%20photograph%20of%20an%20empty%20dim%20classroom%20at%20dusk%20with%20scattered%20exam%20papers%20on%20the%20floor%20and%20cold%20glow%20from%20a%20single%20laptop%20screen%2C%20no%20people%2C%20desaturated%20rust%20and%20charcoal%20tones%2C%20drifting%20haze%2C%20fine%20film%20grain%2C%20melancholic%20documentary%20mood&width=800&height=600&seq=story-teacher-01&orientation=landscape",
    tags: ["人肉搜索", "教师", "断章取义"],
    quote: "我以为把网上的东西删干净了，生活就能回到原样。后来才明白，被拿走的是我对自己生活的解释权。",
    sections: [
      {
        heading: "那段被剪掉的四十二秒",
        paragraphs: [
          "事情的起点是一段四十二秒的课堂视频。视频里，我正对着一个屡次抄袭作业的学生说话，语气重了些。可前因后果——他连续三周交同样的作文、我私下找他谈过两次、当天他当着全班的面把作业本撕了——全都被剪掉了。",
          "视频最先出现在一个本地论坛上，配文是「某中学老师辱骂学生」。我在凌晨两点刷到它的时候，播放量还不到三千。等第二天早上醒过来，它已经出现在五个平台，评论区里有人放出了我的全名，还有人「贴心」地补上了我任教班级和车牌照后四位。",
        ],
      },
      {
        heading: "当现实地址被贴出来",
        paragraphs: [
          "最让我崩溃的不是骂声，而是有人把我家小区的名字写得清清楚楚。那几天我不敢去菜市场，怕被人认出来；晚上听到楼下有说话声就会惊醒。我给物业打了电话，说自己最近可能被人跟踪。电话那头沉默了几秒，问我：「你是不是得罪什么人了？」",
          "学校很快让我先停课回家。我理解这个决定，可那一刻我才意识到，在事情被查清之前，我已经被默认「有问题」了。一个教了十一年书的人，因为一段被剪掉的视频，需要先证明自己配得上讲台。",
        ],
      },
      {
        heading: "删不完，也停不下来",
        paragraphs: [
          "我开始疯狂地删东西。删教案、删朋友圈、删相册里所有和家人有关的照片，连十年来攒的教学笔记都清空了。我以为只要网上找不到我，攻击就会失去目标。可事实是，账号可以被删除，重发只需要几秒钟。",
          "有一晚我盯着屏幕，忽然问自己：我删的到底是谁的东西？那些教案是我一行一行写出来的，那些照片是我真实的生活。把它删掉，像是我在替攻击我的人，先一步抹去自己的存在。",
        ],
      },
      {
        heading: "被听见的那一天",
        paragraphs: [
          "真正让我停下来的是我丈夫的一句话。他一直没说什么，只是有天晚上把手机从我手里拿走，说：「你不是一个人在对付这件事。」那天我第一次把事情从头到尾讲了一遍，讲的时候哭得说不出话，但讲完以后，胸口那种被压住的感觉松了一点。",
          "后来学校调取了完整课堂录像，事实澄清了，那段视频也被下架。但我知道，很多当事人没有我这样的运气——他们有完整的证据，却没有人愿意看。所以我把这段经历写下来，不是要证明我清白，而是想让还在被围攻的人知道：你不是心虚，你只是恰好站在了流量的靶子上。",
        ],
      },
    ],
    impacts: [
      "连续两周入睡困难，需要借助药物才能断断续续睡四五个小时",
      "出现明显的场所回避，不敢独自前往常去的超市与菜市场",
      "对「老师」这个身份产生动摇，一度准备辞职",
      "与家人关系一度紧张，因为我把所有压力都自己扛着",
    ],
  },
  {
    id: "a-clip-without-context",
    title: "一条被剪掉前后文的录音",
    category: "恶意剪辑",
    author: "林晏（化名）",
    role: "应届毕业生",
    date: "2025-03-16",
    readTime: "10 分钟",
    excerpt:
      "入职第一天，同事群里开始流传一段我的录音。录音是真的，可它被剪掉了最关键的十几分钟，只剩下最容易被误解的几句。",
    image:
      "https://readdy.ai/api/search-image?query=conceptual%20editorial%20still%20life%20of%20tangled%20headphones%20resting%20on%20a%20foggy%20windowsill%20with%20rain%20streaks%2C%20warm%20amber%20backlight%2C%20no%20people%2C%20desaturated%20rust%20and%20cream%20tones%2C%20soft%20haze%2C%20fine%20film%20grain%2C%20quiet%20melancholic%20mood&width=800&height=600&seq=story-graduate-01&orientation=landscape",
    tags: ["恶意剪辑", "职场", "应届生"],
    quote: "我没有说谎，但剪辑让我看起来像一个精心设计过谎言的人。",
    sections: [
      {
        heading: "录了音的人，成了被录音的人",
        paragraphs: [
          "那段录音是我自己录的。入职前，我担心口头承诺不算数，就把和 HR 的一段沟通录了下来，本意只是留个底。没想到这份录音被同批入职的一个人听到，又被转发出去，剪成了只有一分半的版本。",
          "被剪掉的十几分钟里，我一直在解释我为什么希望调整项目组；留下的那几句，恰好是我在抱怨工作强度。放在一起看，它就变成了一个「刚入职就背后说公司坏话」的故事。",
        ],
      },
      {
        heading: "自证是一件没有尽头的事",
        paragraphs: [
          "我做的第一件事是把完整录音发到群里。结果评论区说：「完整版是她后来补录的吧。」那一刻我明白，澄清的前提是被相信，而舆论已经先一步把结论写好了。",
          "之后的两周，我几乎每天都在解释。解释得越多，越像在掩饰。我被调到了另一个小组，理由是「避免团队氛围受影响」。没有人正式指责我，可所有人都开始和我保持距离。",
        ],
      },
      {
        heading: "离开，也是一种选择",
        paragraphs: [
          "我在第三个月递交了辞职信。有人说我「逃了」，但只有我自己知道，那不是逃跑，是我第一次为自己的状态做决定。留下来继续证明，只会让我越来越不像自己。",
          "现在回头看，我最想告诉当时的自己：你不必赢得每一场解释。有些战场，离开比留下更清醒。",
        ],
      },
    ],
    impacts: [
      "入职三个月内体重下降近六公斤，食欲明显减退",
      "对任何形式的沟通产生恐惧，开会前会手心出汗",
      "反复检查自己说过的每句话，担心再次被断章取义",
      "最终选择离职，职业生涯起点被迫重置",
    ],
  },
  {
    id: "the-day-my-shop-was-taken-down",
    title: "店铺下架那天，我没有哭",
    category: "举报封禁",
    author: "苏沁（化名）",
    role: "手工饰品店主",
    date: "2024-07-22",
    readTime: "9 分钟",
    excerpt:
      "有人连续七天定时举报我的店铺，理由是「材质造假」。申诉通道关闭的那一刻，我经营了四年的店，从搜索结果里消失了。",
    image:
      "https://readdy.ai/api/search-image?query=conceptual%20editorial%20photograph%20of%20a%20small%20handmade%20jewelry%20shop%20counter%20after%20closing%20hours%2C%20empty%20display%20stands%20and%20a%20handwritten%20closed%20sign%2C%20warm%20amber%20lamp%20glow%2C%20no%20people%2C%20desaturated%20rust%20and%20charcoal%20tones%2C%20soft%20haze%2C%20fine%20film%20grain&width=800&height=600&seq=story-shop-01&orientation=landscape",
    tags: ["举报封禁", "个体经营", "生计"],
    quote: "他们不是要一个说法，他们只是想让我的店消失。而我的沉默，是因为我已经没有力气再去证明什么了。",
    sections: [
      {
        heading: "从一条差评开始",
        paragraphs: [
          "最初只是买家对一款耳环的颜色有异议。我主动提出退款并包邮换货，对方没有回应，却在第二天给了差评，说你家的银是假的。手工饰品的银含量可以做检测，我把供应商的材质证明发过去，对方不再回复。",
          "一周后，我的店铺收到第一封举报通知。之后的七天，每天清晨六点，系统都会推送一条同样模板的举报，理由都是「材质与描述不符」。",
        ],
      },
      {
        heading: "被下架的不是商品，是生活",
        paragraphs: [
          "平台审核期间，店铺被暂停展示。订单无法成交，已付款的订单也面临退单。那是我唯一的收入来源，四年来我靠它还清了开店时的借款。",
          "有人劝我换个名字重新开一家。可店里的每一个链接、每一条评价、每一个关注我的人，都是四年攒下来的。重开的不是店，是从零开始的另一个人生。",
        ],
      },
      {
        heading: "沉默不是认输",
        paragraphs: [
          "我花了一个月提交材料申诉，最后恢复了部分商品，但流量再也没回来。有人说我应该公开发声索赔，我没有。不是不敢，而是那段时间我要先把自己稳住——我还要过日子。",
          "写这些不是想争取同情。我希望的是，平台在处理举报时能多一道人工复核，也希望正在经历同样事情的小店主知道：被恶意举报不是你做错了什么。",
        ],
      },
    ],
    impacts: [
      "四个月收入中断，面临房租与借款双重压力",
      "长期处于高度警觉状态，听到消息提示音就心慌",
      "与信任顾客的联系被切断，社交支持网络同时受损",
      "对「努力就会有回报」的信念产生强烈动摇",
    ],
  },
  {
    id: "performing-a-mother",
    title: "他们说我在「演」一个母亲",
    category: "造谣诽谤",
    author: "何霁（化名）",
    role: "全职妈妈",
    date: "2025-01-05",
    readTime: "11 分钟",
    excerpt:
      "我记录孩子康复过程的一条动态，被解读成「博同情的剧本」。有人翻出我三年前的照片，说我连孩子都是「租来的」。",
    image:
      "https://readdy.ai/api/search-image?query=conceptual%20editorial%20photograph%20of%20a%20quiet%20living%20room%20at%20night%20with%20scattered%20children%20toys%20and%20an%20unlit%20television%20reflecting%20faint%20light%2C%20no%20people%2C%20desaturated%20rust%20and%20cream%20tones%2C%20drifting%20haze%2C%20fine%20film%20grain%2C%20intimate%20melancholic%20mood&width=800&height=600&seq=story-mother-01&orientation=landscape",
    tags: ["造谣诽谤", "母亲", "恶意揣测"],
    quote: "当有人开始怀疑你的悲伤是表演，你连难过都要先自证一遍。",
    sections: [
      {
        heading: "一条普通的记录",
        paragraphs: [
          "我女儿两岁半时被确诊为发育迟缓。我开了个小号，记录康复训练的过程，也给自己找一个能说话的地方。关注我的大多是同城家长，平时互相打气。",
          "转折发生在我发了一段孩子做训练时哭闹的视频之后。有人说，你家条件看着不差，干嘛要「卖惨」；有人翻出我三年前的一张旅游照，说会去旅游的家庭不可能有这种孩子。",
        ],
      },
      {
        heading: "当「证明」成为日常",
        paragraphs: [
          "我开始一件件地证明：就诊记录、诊断书、发票、训练机构的签到表。我把它们拍下来发出去，像在法庭上为自己辩护。可每一次证明之后，都会出现新的质疑：这个诊断书是不是假的？发票是不是 P 的？",
          "最刺痛我的是有人写：孩子是「租来的」，为了拍视频。那几天我看着女儿睡着的样子，第一次觉得害怕——我怕她长大后，会在网上读到这些字。",
        ],
      },
      {
        heading: "把解释权收回来",
        paragraphs: [
          "我最终停更了那个账号，不是因为认输，而是我发现，再多的证明材料也换不来一句「我相信你」。我把精力重新放回到孩子身上，也第一次去见了心理咨询师。",
          "咨询师跟我说了一句话，我记到现在：你无法阻止别人怎么看你，但你可以决定自己还要不要继续站在那个被看的框里。",
        ],
      },
    ],
    impacts: [
      "出现明显的自我怀疑，反复质疑自己的判断与行为",
      "因担心孩子被识别，删除全部家庭影像，产生强烈内疚",
      "与家人多次争吵，家庭氛围长期紧张",
      "停更社交账号，主动切断了原本的支持社群",
    ],
  },
  {
    id: "i-only-shared-a-post",
    title: "我只是转发了一条微博",
    category: "言语围攻",
    author: "岑野（化名）",
    role: "大二学生",
    date: "2025-05-30",
    readTime: "8 分钟",
    excerpt:
      "那天我只是给一条热门微博点了转发。十几个小时后，我的主页被同一种声音填满，私信里全是问候我全家的话。",
    image:
      "https://readdy.ai/api/search-image?query=conceptual%20editorial%20photograph%20of%20a%20dim%20dormitory%20desk%20with%20a%20smartphone%20screen%20glowing%20against%20a%20dark%20wall%20and%20an%20open%20notebook%2C%20no%20people%2C%20desaturated%20rust%20and%20charcoal%20tones%2C%20soft%20fog%2C%20fine%20film%20grain%2C%20restless%20mood&width=800&height=600&seq=story-student-01&orientation=landscape",
    tags: ["言语围攻", "学生", "围观者"],
    quote: "我一直以为网暴离我很远，直到我自己成了那句「活该」指的人。",
    sections: [
      {
        heading: "一次没有立场的转发",
        paragraphs: [
          "那条微博讲的是一个热点事件，双方各执一词。我没仔细看完，只是觉得内容里提到的某一点有道理，就顺手转发了，什么评论都没加。到现在我还记得转发的时间——下午三点十七分。",
          "晚上十点多，我的手机开始不停地震。点进去才发现，两派的网友都找上了我：一边说我「带节奏」，一边说我「站错队」。有人把我转发记录翻了个底朝天，给我总结出一份「立场图谱」。",
        ],
      },
      {
        heading: "被淹没的十几小时",
        paragraphs: [
          "那十几个小时里，我一共收到六百多条私信。开始我还一条条看，试图解释「我只是转发」。后来我发现，没有人关心我转了什么，他们只在找一个可以出气的账号。",
          "我删掉了转发，又删掉了所有动态，最后把账号设成了私密。做完这些，我坐在宿舍床上，忽然不知道该不该去上第二天的课。",
        ],
      },
      {
        heading: "学会不再当那个节点",
        paragraphs: [
          "这件事没有持续太久，一周后舆论转向了别的事件，我的名字也沉了下去。但它留给我的习惯很难改：现在我转发任何东西之前，都会先看完、先想清楚。",
          "我把这段经历写下来，是想告诉和我一样喜欢随手转发的人：你以为的一次转发只是一个小小的动作，但它可能是别人一天里第一百次被看见。少当一次节点，可能就少一轮扩散。",
        ],
      },
    ],
    impacts: [
      "连续多日高度紧张，手机一响就心跳加速",
      "一度害怕去上课，对人群产生回避心理",
      "对公开发言产生长期顾虑，社交账号转为私密",
      "开始理解「围观者也是传播链一部分」这一事实",
    ],
  },
  {
    id: "when-my-colleagues-became-part-of-it",
    title: "当我的同事也变成了「黑料」的一部分",
    category: "舆论株连",
    author: "江穗（化名）",
    role: "杂志编辑",
    date: "2025-06-18",
    readTime: "10 分钟",
    excerpt:
      "一篇被指「洗稿」的文章署名是我。攻击很快越过我，烧到了曾和我合作过的作者、审稿编辑，甚至是转发过我文章的老师。",
    image:
      "https://readdy.ai/api/search-image?query=conceptual%20editorial%20photograph%20of%20an%20empty%20magazine%20editorial%20office%20with%20tilted%20chairs%20and%20a%20wall%20of%20draft%20boards%20casting%20long%20shadows%2C%20no%20people%2C%20desaturated%20rust%20and%20charcoal%20tones%2C%20drifting%20haze%2C%20fine%20film%20grain%2C%20subdued%20mood&width=800&height=600&seq=story-editor-01&orientation=landscape",
    tags: ["舆论株连", "职场", "连带伤害"],
    quote: "攻击我，我可以承受；可当它顺着我的通讯录往下烧，我才真正感到害怕。",
    sections: [
      {
        heading: "争议从文章开始，伤害从关系蔓延",
        paragraphs: [
          "那篇文章是我负责的选题，采访了七个人。文章发出后，有人贴出另一篇早两年的旧文，说结构高度相似，指控我们「洗稿」。编辑部启动了内部核查，我配合提供了全部采访记录和修改稿。",
          "可舆论没有等核查结果。有人把我的工作邮箱、合作作者名单整理出来，逐个去「提醒」。我的一位老作者收到上百条私信，被质问「为什么和这种人合作」。",
        ],
      },
      {
        heading: "替我受过的那些人",
        paragraphs: [
          "最让我难受的，不是自己被骂，而是那些本来与选题无关的人被拖进来。一位只帮我转发过文章的高校老师，被追着要求「表态」。他给我发消息说没事，可我知道他那一周都没睡好。",
          "我给每一个被牵连的人单独道歉。虽然理性上我知道这不是我的错，但情感上，那种「因为认识我而受伤害」的内疚，反复地折磨着我。",
        ],
      },
      {
        heading: "把责任还给责任本身",
        paragraphs: [
          "核查最终结论是选题策划存在流程瑕疵，但不存在主观抄袭。编辑部公开了说明，也调整了选题流程。争议慢慢平息了，但我和一些同事的关系，再也回不到从前那种自然。",
          "现在我会提醒身边的人：如果你决定参与一次公开的攻击，请想清楚你针对的到底是某个人，还是与这个人相关的所有关系。株连，恰恰是黑料最有效、也最容易被忽略的武器。",
        ],
      },
    ],
    impacts: [
      "长期内疚与自责，认为自己是连累他人的源头",
      "与同事、合作者的信任关系出现难以修复的裂痕",
      "出现持续的职业倦怠，对选题工作产生抵触",
      "重新评估「公开发表」这件事对周围人的连带风险",
    ],
  },
];